'use client'

import { uploadFileToService } from '@/lib/utils/fileUpload'
import { Upload, X } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useId, useRef, useState } from 'react'

interface ImageUploadProps {
  value?: string
  onChange: (url: string) => void
  onError?: (error: string) => void
  className?: string
  placeholder?: string
}

export function ImageUpload({
  value,
  onChange,
  onError,
  className = '',
  placeholder = 'Upload logo',
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const uniqueId = useId()

  const handleFileUpload = useCallback(
    async (file: File) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        onError?.('Please upload an image file')
        return
      }

      // Validate file size (5MB limit for images)
      if (file.size > 5 * 1024 * 1024) {
        onError?.('Image size must be less than 5MB')
        return
      }

      setIsUploading(true)
      setUploadProgress(0)

      try {
        const result = await uploadFileToService(file, (progress) => {
          setUploadProgress(progress)
        })
        if (result.success && result.url) {
          onChange(result.url)
          setUploadProgress(100)
        } else {
          onError?.(result.error || 'Upload failed')
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed'
        onError?.(errorMessage)
      } finally {
        setIsUploading(false)
      }
    },
    [onChange, onError]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileUpload(file)
      }
    },
    [handleFileUpload]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragActive(false)

      const file = e.dataTransfer.files?.[0]
      if (file) {
        handleFileUpload(file)
      }
    },
    [handleFileUpload]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }, [])

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange('')
    },
    [onChange]
  )

  const handleClick = useCallback(() => {
    if (!isUploading && inputRef.current) {
      inputRef.current.click()
    }
  }, [isUploading])

  return (
    <div className={`relative ${className}`}>
      {value ? (
        // Show uploaded image
        <div className="group relative">
          <div className="relative h-24 w-24 overflow-hidden rounded-lg border border-gray-200">
            <Image
              src={value}
              alt="Uploaded logo"
              width={96}
              height={96}
              className="h-full w-full object-cover"
              onError={() => onError?.('Failed to load image')}
            />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -right-2 -top-2 z-10 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        // Show upload area
        <div
          className={`relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed transition-colors ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'} ${isUploading ? 'cursor-not-allowed opacity-50' : ''} `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
        >
          {/* Upload progress bar */}
          {isUploading && uploadProgress < 100 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b bg-gray-100">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          <div className="flex flex-col items-center gap-1">
            <Upload className="h-6 w-6 text-gray-400" />
            <span className="text-center text-xs text-gray-500">
              {isUploading ? `${uploadProgress}%` : placeholder}
            </span>
          </div>
          <input
            ref={inputRef}
            id={uniqueId}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={isUploading}
            className="hidden"
          />
        </div>
      )}
    </div>
  )
}
